import {
  Accessor,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
  Setter,
  Show,
} from "solid-js";
import { CloseModalButton } from "~/components/modal";
import { createJob } from "~/utils/job";
import { Spacer } from "~/widgets/spacer";
import "~/components/gifting/verification/collect_otp.css";
import { useIsRouting } from "@solidjs/router";
import { clientRepo } from "~/server/apis/client_repo";
import { ButtonDarkV2 } from "~/components/login-rnr/login_rnr_code_component";
import { showSnackBar } from "~/shared_states/snackbar";
import { goBack } from "~/shared_states/modal";

export function getBrandL2OtpVerificationComponent(props: {
  phoneNumber: string;
  otpToken: string;
  sendOtp: () => Promise<{ otpToken: string }>;
  onVerificationSuccess: (response: any) => void;
  onError?: (error: string) => void;
}) {
  return <BrandL2OtpVerification {...props} />;
}

export type BrandL2OtpVerificationProps = {
  phoneNumber: string;
  otpToken: string;
  sendOtp: () => Promise<{ otpToken: string }>;
  onVerificationSuccess: (response: any) => void;
  onError?: (error: string) => void;
};

export function BrandL2OtpVerification(props: BrandL2OtpVerificationProps) {
  const [otp, setOtp] = createSignal<string>("");
  const [errorMessage, setErrorMessage] = createSignal<string | undefined>("");
  const [resendOtpTimeout, setResendOtpTimeout] = createSignal<number>(30);
  const [otpToken, setOtpToken] = createSignal<string>(props.otpToken);

  let resendOtpInterval: NodeJS.Timeout | undefined;
  const ac = new AbortController();
  const isRouting = useIsRouting();

  function resetSendOtpInterval() {
    setResendOtpTimeout(30);
    resendOtpInterval = setInterval(async () => {
      setResendOtpTimeout(resendOtpTimeout() - 1);

      if (resendOtpTimeout() == 0) {
        clearInterval(resendOtpInterval);
      }
    }, 1000);
  }

  resetSendOtpInterval();

  const submitOtpJob = createJob<any>({
    errorCallback: (error) => {
      if (otp().length == 6) {
        setOtp("");
      }
      setErrorMessage(error.message);
      props.onError?.(error.message);
    },
    initialJob: async () => {
      ac.abort();
      if (otp().length != 6) {
        throw new Error("Please enter a 6 digit OTP");
      }
      return clientRepo.confirmPhoneUpdate(otp(), otpToken());
    },
    successCallback: async (response) => {
      return props.onVerificationSuccess(response);
    },
  });

  let phoneInput: HTMLInputElement | undefined;
  createEffect(() => {
    phoneInput?.focus();
  });

  createEffect(() => {
    if (otp().length == 6) {
      submitOtpJob.run();
    }

    setErrorMessage(undefined);
  });

  onCleanup(() => {
    clearInterval(resendOtpInterval);
    ac.abort();
  });

  onMount(() => {
    // Auto-read SMS functionality can be added here if needed
  });

  return (
    <div id="verify-otp-brandl2-modal" class=" bg-baseTertiaryDark p-2">
      <div class="relative rounded-2xl bg-baseTertiaryLight px-4 py-6">
        <div class=" absolute right-4 top-4 bg-baseTertiaryLight lg:right-6 lg:top-6">
          <CloseModalButton
            onClick={() => {
              goBack();
            }}
          />
        </div>
        <TitleAndSubtitle />
        <div class="h-5 sm:h-6" />
        <InputField />
        <Spacer height={16} />
        <Show when={resendOtpTimeout() == 0} fallback={<ResendOtpTimer />}>
          <ResendOtpText />
        </Show>
        <Spacer height={32} />
        <ButtonDarkV2
          onClick={() => {
            if (submitOtpJob.jobState() == "running") return;
            submitOtpJob.run();
          }}
          isLoading={submitOtpJob.jobState() == "running" || isRouting()}
        >
          Continue
        </ButtonDarkV2>
      </div>
    </div>
  );

  function TitleAndSubtitle() {
    return (
      <div class="flex flex-col items-start">
        <span class="text-center text-h4 text-textDark">Verify OTP</span>
        <Spacer height={4} />
        <div class="flex cursor-pointer flex-row">
          <span class="text-medium text-textNormal">
            Sent to{" "}
            <span class="border-b border-[#7c7c7c]">{props.phoneNumber}</span>
          </span>

          <Spacer width={4} />
        </div>
      </div>
    );
  }

  function ResendOtpTimer() {
    return (
      <span class="text-medium leading-[140%] text-textNormal underline-offset-2">
        Resend OTP in {resendOtpTimeout()}s
      </span>
    );
  }

  function ResendOtpText() {
    return (
      <div
        onClick={() => {
          props
            .sendOtp()
            .then((res) => {
              setOtpToken(res.otpToken);
              resetSendOtpInterval();
              showSnackBar({
                level: "success",
                message: "OTP sent successfully",
              });
            })
            .catch((err) => {
              showSnackBar({
                level: "error",
                message: err.message ?? "Something went wrong",
              });
            });
        }}
      >
        <div class="flex h-[18px] items-center ">
          <span class="cursor-pointer text-medium leading-[140%] text-textNormal underline-offset-2">
            Didn't get OTP? <u>Resend OTP</u>
          </span>
        </div>
      </div>
    );
  }

  function editIcon() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
      >
        <path
          d="M3.75911 12.7232L4.39947 12.9366H4.39947L3.75911 12.7232ZM4.321 11.0375L4.96136 11.2509H4.96136L4.321 11.0375ZM4.90078 10.0994L5.37808 10.5767L4.90078 10.0994ZM8.90163 6.09853L9.37893 6.57582V6.57582L8.90163 6.09853ZM10.5987 6.09853L10.1214 6.57582L10.1214 6.57583L10.5987 6.09853ZM11.9016 7.40147L12.3789 6.92418L11.9016 7.40147ZM11.9016 9.09853L12.3789 9.57583L11.9016 9.09853ZM7.90078 13.0994L7.42348 12.6221H7.42348L7.90078 13.0994ZM6.96267 13.6792L7.17612 14.3195H7.17612L6.96267 13.6792ZM5.277 14.2411L5.49045 14.8814H5.49045L5.277 14.2411ZM7.59325 13.3864L7.18673 12.8476L7.18673 12.8476L7.59325 13.3864ZM7.35694 13.5325L7.05677 12.9279L7.35694 13.5325ZM12.4028 8.01824L13.0448 7.80965L13.0448 7.80965L12.4028 8.01824ZM12.4028 8.48176L13.0448 8.69035L13.0448 8.69035L12.4028 8.48176ZM9.5184 5.59737L9.30981 4.9554L9.30981 4.9554L9.5184 5.59737ZM9.98192 5.59737L10.1905 4.9554L9.98192 5.59737ZM4.46769 10.6432L5.07227 10.9434L5.07227 10.9434L4.46769 10.6432ZM4.61373 10.4069L4.07487 10.0004L4.07487 10.0004L4.61373 10.4069ZM3.47958 14.0661L2.8438 14.2928L3.47958 14.0661ZM3.9341 14.5206L3.70738 15.1564H3.70739L3.9341 14.5206ZM11.7275 3.2727C11.4639 3.0091 11.0365 3.0091 10.7729 3.2727C10.5093 3.53631 10.5093 3.96369 10.7729 4.2273L11.7275 3.2727ZM13.7729 7.2273C14.0365 7.4909 14.4639 7.4909 14.7275 7.2273C14.9911 6.96369 14.9911 6.53631 14.7275 6.2727L13.7729 7.2273ZM4.39947 12.9366L4.96136 11.2509L3.68064 10.824L3.11874 12.5097L4.39947 12.9366ZM5.37808 10.5767L9.37893 6.57582L8.42433 5.62123L4.42348 9.62208L5.37808 10.5767ZM10.1214 6.57583L11.4243 7.87877L12.3789 6.92418L11.076 5.62123L10.1214 6.57583ZM11.4243 8.62123L7.42348 12.6221L8.37808 13.5767L12.3789 9.57583L11.4243 8.62123ZM6.74922 13.0388L5.06355 13.6007L5.49045 14.8814L7.17612 14.3195L6.74922 13.0388ZM7.42348 12.6221C7.26133 12.7842 7.22403 12.8194 7.18673 12.8476L7.99976 13.9253C8.1293 13.8276 8.2414 13.7133 8.37808 13.5767L7.42348 12.6221ZM7.17612 14.3195C7.35949 14.2584 7.51178 14.2092 7.65712 14.137L7.05677 12.9279C7.01493 12.9487 6.96677 12.9663 6.74922 13.0388L7.17612 14.3195ZM7.18673 12.8476C7.14599 12.8783 7.10248 12.9052 7.05677 12.9279L7.65712 14.137C7.77764 14.0772 7.89234 14.0063 7.99976 13.9253L7.18673 12.8476ZM11.4243 7.87877C11.5807 8.03515 11.6689 8.1241 11.7281 8.19386C11.7822 8.25759 11.7715 8.2597 11.7608 8.22683L13.0448 7.80965C12.9784 7.60553 12.8656 7.44776 12.7573 7.32017C12.6541 7.19861 12.5196 7.06481 12.3789 6.92418L11.4243 7.87877ZM12.3789 9.57583C12.5196 9.43519 12.6541 9.30139 12.7573 9.17983C12.8656 9.05224 12.9784 8.89447 13.0448 8.69035L11.7608 8.27317C11.7715 8.2403 11.7822 8.24241 11.7281 8.30614C11.6689 8.3759 11.5807 8.46485 11.4243 8.62123L12.3789 9.57583ZM11.7608 8.22682C11.7657 8.24189 11.7657 8.25811 11.7608 8.27318L13.0448 8.69035C13.1377 8.40415 13.1377 8.09585 13.0448 7.80965L11.7608 8.22682ZM9.37893 6.57582C9.53531 6.41944 9.62426 6.33123 9.69402 6.27201C9.75775 6.21791 9.75986 6.22865 9.72698 6.23933L9.30981 4.9554C9.10569 5.02173 8.94792 5.13454 8.82033 5.24286C8.69877 5.34605 8.56496 5.4806 8.42433 5.62123L9.37893 6.57582ZM11.076 5.62123C10.9354 5.4806 10.8015 5.34605 10.68 5.24285C10.5524 5.13454 10.3946 5.02173 10.1905 4.9554L9.77334 6.23933C9.74046 6.22865 9.74257 6.21791 9.8063 6.27201C9.87605 6.33123 9.96501 6.41944 10.1214 6.57582L11.076 5.62123ZM9.72698 6.23933C9.74205 6.23444 9.75827 6.23444 9.77334 6.23933L10.1905 4.9554C9.90431 4.86241 9.59601 4.86241 9.30981 4.9554L9.72698 6.23933ZM4.96136 11.2509C5.03387 11.0334 5.0515 10.9852 5.07227 10.9434L3.86311 10.343C3.79095 10.4884 3.74176 10.6407 3.68064 10.824L4.96136 11.2509ZM4.42348 9.62208C4.28681 9.75875 4.17259 9.87086 4.07487 10.0004L5.1526 10.8134C5.18073 10.7761 5.21592 10.7388 5.37808 10.5767L4.42348 9.62208ZM5.07227 10.9434C5.09497 10.8977 5.12186 10.8542 5.1526 10.8134L4.07487 10.0004C3.99384 10.1078 3.92295 10.2225 3.86311 10.343L5.07227 10.9434ZM3.11874 12.5097C3.00086 12.8634 2.89699 13.1728 2.83806 13.4236C2.78148 13.6644 2.73188 13.9789 2.8438 14.2928L4.11537 13.8393C4.1404 13.9095 4.10843 13.919 4.15226 13.7324C4.19374 13.5559 4.27308 13.3158 4.39947 12.9366L3.11874 12.5097ZM5.06355 13.6007C4.68437 13.7271 4.44423 13.8064 4.26771 13.8479C4.08117 13.8917 4.09064 13.8598 4.16082 13.8848L3.70739 15.1564C4.02123 15.2683 4.33574 15.2187 4.57653 15.1621C4.82733 15.1032 5.13681 14.9993 5.49045 14.8814L5.06355 13.6007ZM2.8438 14.2928C2.98746 14.6957 3.3045 15.0127 3.70738 15.1564L4.16082 13.8848C4.13962 13.8772 4.12293 13.8605 4.11537 13.8393L2.8438 14.2928ZM10.7729 4.2273L13.7729 7.2273L14.7275 6.2727L11.7275 3.2727L10.7729 4.2273Z"
          fill="#7C7C7C"
        />
      </svg>
    );
  }
  function InputField() {
    return (
      <div>
        <OtpInput otp={otp} setOtp={setOtp} />
        <Spacer height={48} />
        <Show when={errorMessage()}>
          <Spacer height={8} />
          <span class="text-medium  text-errorDark">{errorMessage()}</span>
        </Show>
      </div>
    );
  }

  function OtpInput(props: { otp: Accessor<string>; setOtp: Setter<string> }) {
    const [hasFocus, setHasFocus] = createSignal<boolean>(false);

    let otpInput: HTMLInputElement | undefined;
    createEffect(async () => {
      otpInput?.focus();
    });

    return (
      <div class="relative">
        <input
          id="otp-input-brandl2"
          ref={otpInput}
          class="absolute z-10 h-16 w-full bg-transparent text-transparent  caret-transparent"
          inputmode="numeric"
          autocomplete="one-time-code"
          pattern="\d{6}"
          maxlength={6}
          value={props.otp()}
          placeholder=""
          onInput={(e) => props.setOtp(e.currentTarget.value)}
          onFocus={() => setHasFocus(true)}
          onBlur={() => setHasFocus(false)}
        />
        <div class=" pointer-events-none absolute z-20  flex w-full items-center justify-around space-x-2  lg:space-x-3">
          {[...Array(6)].map((_, index) => (
            <div
              class={`flex h-[52px]  flex-1 cursor-text items-center justify-center rounded-[12px] border border-basePrimaryLight text-basePrimaryDark`}
              classList={{
                "border-[#fff]": index <= props.otp().length,
                "border-errorDark": !!errorMessage(),
              }}
            >
              {props.otp()[index] ? (
                props.otp()[index]
              ) : index == props.otp().length && hasFocus() ? (
                <div class={`animateBlink h-3 w-0.5 !bg-basePrimaryDark`} />
              ) : (
                <div class="h-1 w-1 rounded-full bg-baseSecondaryLight" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
}
